import { getElementsByClassName } from '../utils';
import { useState } from '../elements/state';
import { FormGroup, Input, Select } from '../elements/inputs';
import jsx from '../jsx';

type DealerType = 'Dealer' | 'Non-Dealer';
type WinType = 'Tsumo' | 'Ron';
type WaitType =
  | 'Open wait'
  | 'Dual pair wait'
  | 'Closed wait'
  | 'Edge wait'
  | 'Pair wait';

const [getFu, setFu] = useState('20');
const [getHan, setHan] = useState('0');

const [getOpenTripletS, setOpenTripletS] = useState('0');
const [getOpenTripletTH, setOpenTripletTH] = useState('0');

const [getClosedTripletS, setClosedTripletS] = useState('0');
const [getClosedTripletTH, setClosedTripletTH] = useState('0');

const [getOpenKanS, setOpenKanS] = useState('0');
const [getOpenKanTH, setOpenKanTH] = useState('0');

const [getClosedKanS, setClosedKanS] = useState('0');
const [getClosedKanTH, setClosedKanTH] = useState('0');

const [getWait, setWait] = useState<WaitType>('Open wait');
const [getWinType, setWinType] = useState<WinType>('Tsumo');
const [getDealer, setDealer] = useState<DealerType>('Dealer');

const calculateFu = (): number => {
  const multipliers = [2, 4, 8, 16, 4, 8, 16, 32];
  const values = [
    getOpenTripletS(),
    getOpenTripletTH(),
    getOpenKanS(),
    getOpenKanTH(),
    getClosedTripletS(),
    getClosedTripletTH(),
    getClosedKanS(),
    getClosedKanTH(),
  ].map((value) => parseInt(value, 10));

  let waitFu = 0;
  switch (getWait()) {
    case 'Open wait':
    case 'Dual pair wait':
      waitFu = 0;
      break;
    case 'Closed wait':
    case 'Edge wait':
    case 'Pair wait':
      waitFu = 2;
      break;
  }

  const winTypeFu = getWinType() === 'Tsumo' ? 2 : 10;
  const groupsFu = values.reduce((acc, value, index) => {
    const multiplier = multipliers[index];
    return acc + value * multiplier;
  }, 20);

  return Math.ceil((groupsFu + winTypeFu + waitFu) / 10) * 10;
};

const createFuCalculator = () => {
  const container = <div />;

  container.append(
    FormGroup('Open Triplet', [
      Input({
        placeholder: 'Simples',
        type: 'number',
        onChange: setOpenTripletS,
      }),
      Input({
        placeholder: 'Terminal/Honor',
        type: 'number',
        onChange: setOpenTripletTH,
      }),
    ]),
    FormGroup('Open Kan', [
      Input({
        placeholder: 'Simples',
        type: 'number',
        onChange: setOpenKanS,
      }),
      Input({
        placeholder: 'Terminal/Honor',
        type: 'number',
        onChange: setOpenKanTH,
      }),
    ]),
    FormGroup('Closed Triplet', [
      Input({
        placeholder: 'Simples',
        type: 'number',
        onChange: setClosedTripletS,
      }),
      Input({
        placeholder: 'Terminal/Honor',
        type: 'number',
        onChange: setClosedTripletTH,
      }),
    ]),
    FormGroup('Closed Kan', [
      Input({
        placeholder: 'Simples',
        type: 'number',
        onChange: setClosedKanS,
      }),
      Input({
        placeholder: 'Terminal/Honor',
        type: 'number',
        onChange: setClosedKanTH,
      }),
    ]),
    FormGroup('Wait', [
      Select<WaitType>({
        value: getWait(),
        options: [
          'Open wait',
          'Dual pair wait',
          'Closed wait',
          'Edge wait',
          'Pair wait',
        ],
        onChange: setWait,
      }),
    ]),
    FormGroup('Win type', [
      Select<WinType>({
        value: getWinType(),
        options: ['Tsumo', 'Ron'],
        className: 'winTypeFu',
        onChange: (winType) => {
          setWinType(winType);
          getElementsByClassName<HTMLSelectElement>('winTypeScore')[0].value =
            winType;
        },
      }),
    ]),
    <button
      onclick={() => {
        const fu = calculateFu();
        setFu(fu.toString());
        getElementsByClassName<HTMLDivElement>('fuOutput')[0].innerText =
          `${fu.toString()} fu`;
        getElementsByClassName<HTMLInputElement>('fuInput')[0].value =
          fu.toString();
      }}
    >
      Calculate
    </button>,
    <p className="fuOutput" style="margin-top: 12px; font-size: 1.25em;" />,
  );

  return container;
};

const getScore = (): string => {
  const isWinTsumo = getWinType() === 'Tsumo';
  const isDealer = getDealer() === 'Dealer';
  const han = parseInt(getHan(), 10);
  const fu = parseInt(getFu(), 10);

  if (han >= 13) {
    // Yakuman
    if (isDealer) {
      return isWinTsumo ? '1600' : '48000';
    } else {
      return isWinTsumo ? '8000/16000' : '32000';
    }
  }

  if (han >= 11) {
    // Sanbaiman
    if (isDealer) {
      return isWinTsumo ? '12000' : '36000';
    } else {
      return isWinTsumo ? '6000/12000' : '24000';
    }
  }

  if (han >= 8) {
    // Baiman
    if (isDealer) {
      return isWinTsumo ? '8000' : '24000';
    } else {
      return isWinTsumo ? '4000/8000' : '16000';
    }
  }

  if (han >= 6) {
    // Haneman
    if (isDealer) {
      return isWinTsumo ? '6000' : '18000';
    } else {
      return isWinTsumo ? '3000/6000' : '12000';
    }
  }

  if (han === 5) {
    // Mangan
    if (isDealer) {
      return isWinTsumo ? '4000' : '12000';
    } else {
      return isWinTsumo ? '2000/4000' : '8000';
    }
  }

  const basePoints = fu * Math.pow(2, 2 + han);
  if (isDealer) {
    const calculatedPoints = isWinTsumo ? basePoints * 2 : basePoints * 6;
    return `${Math.ceil(calculatedPoints / 100) * 100}`;
  } else {
    if (isWinTsumo) {
      const nonDealerPoints = basePoints;
      const dealerPoints = basePoints * 2;
      return `${Math.ceil(nonDealerPoints / 100) * 100}/${Math.ceil(dealerPoints / 100) * 100}`;
    } else {
      const calculatedPoints = basePoints * 4;
      return `${Math.ceil(calculatedPoints / 100) * 100}`;
    }
  }
};

const createScoreCalculator = () => {
  const container = <div />;

  const formGroup = FormGroup('Han', [
    Input({
      placeholder: 'Han',
      type: 'number',
      onChange: setHan,
    }),
  ]);

  formGroup.append(
    <button
      onclick={() => {
        const score = getScore();
        getElementsByClassName<HTMLDivElement>('scoreOutput')[0].innerText =
          `${score} Points`;
      }}
    >
      Calculate
    </button>,
    <p className="scoreOutput" style="margin-top: 12px; font-size: 1.25em;" />,
  );

  container.append(
    FormGroup('Seat', [
      Select<DealerType>({
        value: getDealer(),
        options: ['Dealer', 'Non-Dealer'],
        onChange: setDealer,
      }),
    ]),
    FormGroup('Win type', [
      Select<WinType>({
        value: getWinType(),
        options: ['Tsumo', 'Ron'],
        className: 'winTypeScore',
        onChange: (winType) => {
          setWinType(winType);
          getElementsByClassName<HTMLSelectElement>('winTypeFu')[0].value =
            winType;
        },
      }),
    ]),
    FormGroup('Fu', [
      Input({
        className: 'fuInput',
        type: 'number',
        placeholder: 'Fu',
        onChange: setFu,
      }),
    ]),
    formGroup,
  );

  return container;
};

document.addEventListener('DOMContentLoaded', () => {
  const riichiScoring = document.querySelector('.riichiScoring');

  if (riichiScoring) {
    riichiScoring.append(
      <h2>Fu Calculator</h2>,
      <p>
        Enter the values below to calculate your hands fu. The default value is
        zero!
      </p>,
      createFuCalculator(),
      <h2>Score</h2>,
      <p>
        Use the following table to calculate your score, invalid han/fu
        combinations will return values.
      </p>,
      createScoreCalculator(),
    );
  }
});
